/** Cumulative layered visibility — mirrors backend layout.py */

import { applyListLayout, elementListLevel, formatListDisplay } from './listFormat';
import { isNarrationGatedElement as isNarrationGatedLayer } from './layerTypes';
import {
  activeNarratorCueAtPlayhead,
  codeHighlightFromNarratorHistory,
  flowchartStepIndexAtPlayhead,
} from './narratorSync';

export const DESIGN_W = 1280;
export const DESIGN_H = 720;

const ZONES = {
  scene_title: { x: 64, y: 48, max_width: 1152, w: 800, h: 56 },
  explanation: { x: 64, y: 140, max_width: 480, w: 480, h: 120 },
  bullet: { x: 64, y: 140, max_width: 480, w: 480, h: 48 },
  keyword: { x: 64, y: 120, max_width: 400, w: 320, h: 32 },
  console_output: { x: 64, y: 580, max_width: 520, w: 520, h: 80 },
  image: { x: 720, y: 200, w: 480, h: 270, max_width: 480 },
};

export const DEFAULT_CODE_PANEL = { x: 580, y: 120, w: 660, h: 400 };

export function elementNarrationStart(el) {
  if (el?.narration_start != null) return Number(el.narration_start);
  return Number(el?.visible_from ?? el?.start ?? 0);
}

export function elementScreenplayVisible(el, t) {
  if (!el?.screenplay_gated) return true;
  const vf = el.screenplay_visible_from;
  if (vf == null) return true;
  const end = Number(el.end ?? el.hold_frame_until ?? vf + 0.001);
  return Number(vf) <= t && t < end;
}

export function highlightLineAtTime(rendered, t) {
  const events = rendered?.screenplay_events || [];
  let active = null;
  let activeStart = -1;
  for (const evt of events) {
    if (evt.action !== 'highlight_line') continue;
    const t0 = Number(evt.global_time) || 0;
    const t1 = Number(evt.global_end ?? t0 + 0.001);
    if (t0 <= t && t < t1 && t0 >= activeStart) {
      activeStart = t0;
      active = Number(evt.payload?.line) || 1;
    }
  }
  if (active != null) return active;
  for (const el of rendered?.elements || []) {
    if (el.type !== 'code_highlight') continue;
    const t0 = Number(el.screenplay_highlight_from ?? el.visible_from ?? 0);
    const t1 = Number(el.screenplay_highlight_until ?? el.end ?? t0 + 0.001);
    if (t0 <= t && t < t1) return Number(el.highlight_line) || 1;
  }
  return null;
}

export function upcomingScreenplayEvents(rendered, t, limit = 3) {
  const events = [...(rendered?.screenplay_events || [])].sort(
    (a, b) => Number(a.global_time) - Number(b.global_time),
  );
  return events.filter((evt) => Number(evt.global_time) >= t - 0.05).slice(0, limit);
}

/** Layer visibility: intro/term/gloss at slide start; artifacts & code steps at narration start. */
export function elementVisibleStart(el, scene = null) {
  if (el?.screenplay_gated && el.screenplay_visible_from != null) {
    return Number(el.screenplay_visible_from);
  }
  if (isNarrationGatedLayer(el)) {
    return elementNarrationStart(el);
  }
  if (el?.sync_visual_at_scene_start) {
    if (scene != null) return Number(scene.start ?? 0);
    if (el?.visible_from != null) return Number(el.visible_from);
  }
  return elementNarrationStart(el);
}

export function elementNarrationEnd(el, rendered = null) {
  const timings = rendered?.narration_timings;
  if (timings?.length && el?.id) {
    const row = timings.find((t) => String(t.element_id) === String(el.id));
    if (row?.narration_end != null) return Number(row.narration_end);
  }
  return Number(el?.narration_end ?? el?.audio_end ?? el?.end ?? elementNarrationStart(el));
}

export function elementNarrationStartFromTimings(el, rendered) {
  const timings = rendered?.narration_timings;
  if (timings?.length && el?.id) {
    const row = timings.find((t) => String(t.element_id) === String(el.id));
    if (row?.start != null) return Number(row.start);
  }
  return elementNarrationStart(el);
}

/** Copy stored TTS timings onto elements so preview matches the narration MP3. */
export function syncRenderedFromNarrationTimings(rendered) {
  if (!rendered?.narration_timings?.length || !rendered?.elements?.length) return rendered;
  const byId = Object.fromEntries(
    rendered.narration_timings.map((t) => [String(t.element_id), t]),
  );
  for (const el of rendered.elements) {
    const row = byId[String(el.id)];
    if (!row) continue;
    const start = Number(row.start);
    const end = Number(row.narration_end);
    el.narration_start = start;
    el.narration_end = end;
    el.audio_end = end;
    el.start = start;
    if (row.audio_duration != null) el.audio_duration = Number(row.audio_duration);
    if (isNarrationGatedLayer(el)) {
      el.sync_visual_at_scene_start = false;
      el.visible_from = start;
    } else if (!el.sync_visual_at_scene_start) {
      el.visible_from = start;
    }
  }
  return rendered;
}

export function elementAudioDuration(el) {
  if (el?.audio_duration != null) return Number(el.audio_duration);
  return Math.max(0.001, elementNarrationEnd(el) - elementNarrationStart(el));
}

/** Freeze motion at last frame of block audio while layer stays visible. */
export function renderTimeForElement(el, t, fps = 30) {
  const narrStart = elementNarrationStart(el);
  const narrEnd = elementNarrationEnd(el);
  const layerEnd = Number(el?.end ?? narrEnd);
  if (t >= narrEnd && t < layerEnd - 1e-6) {
    return Math.max(narrStart, narrEnd - 1 / Math.max(fps, 1));
  }
  return t;
}

export function sceneAtTime(scenes, t) {
  for (const sc of scenes || []) {
    const start = Number(sc.start) || 0;
    const end = Number(sc.end) || 0;
    if (t >= start && t < end) return sc;
  }
  const last = (scenes || [])[(scenes || []).length - 1];
  if (last && t >= Number(last.start)) return last;
  return null;
}

const SKIP_NARRATION_TYPES = new Set(['scene_transition', 'code_highlight', 'flowchart_arrow']);
const PLAYBACK_NARRATION_EPS = 0.02;

function elementHasNarration(el) {
  if (SKIP_NARRATION_TYPES.has(el?.type)) return false;
  return Boolean(String(el?.narration || '').trim());
}

/** Global spoken-block order (scene order → block_ids). */
export function narratedBlocksInOrder(elements, scenes) {
  const byId = Object.fromEntries(
    (elements || []).filter((e) => e.id).map((e) => [String(e.id), e]),
  );
  const order = [];
  for (const sc of scenes || []) {
    for (const bid of sc.block_ids || []) {
      const el = byId[String(bid)];
      if (el && elementHasNarration(el)) order.push(el);
    }
  }
  return order;
}

function sceneForBlockId(blockId, scenes) {
  const bid = String(blockId);
  for (const sc of scenes || []) {
    if ((sc.block_ids || []).map(String).includes(bid)) return sc;
  }
  return null;
}

function sceneIndexForBlock(blockId, scenes) {
  const bid = String(blockId);
  for (let i = 0; i < (scenes || []).length; i += 1) {
    if ((scenes[i].block_ids || []).map(String).includes(bid)) return i;
  }
  return -1;
}

/** True when every earlier spoken block's audio has finished at t. */
export function priorNarrationComplete(el, narratedOrder, t, rendered = null, eps = PLAYBACK_NARRATION_EPS) {
  const eid = String(el?.id || '');
  for (const prior of narratedOrder) {
    if (String(prior.id) === eid) return true;
    if (elementNarrationEnd(prior, rendered) > t + eps) return false;
  }
  return true;
}

export function elementMayRenderAt(el, scene, t, narratedOrder, scenes = null, rendered = null) {
  if (el?.screenplay_gated && !elementScreenplayVisible(el, t)) return false;
  if (!priorNarrationComplete(el, narratedOrder, t, rendered)) return false;
  if (scenes?.length) {
    const si = sceneIndexForBlock(el.id, scenes);
    if (si > 0 && t + PLAYBACK_NARRATION_EPS < (Number(scenes[si].start) || 0)) return false;
  }
  const visStart = elementVisibleStart(el, scene);
  const end = Number(el.end) || visStart + 0.001;
  return visStart <= t && t < end;
}

/** Scene for clip at t, or last scene whose audio finished — no early topic advance. */
export function sceneAtPlayhead(scenes, t, elements = null, rendered = null) {
  if (!elements?.length || !scenes?.length) return sceneAtTime(scenes, t);

  const order = narratedBlocksInOrder(elements, scenes);
  if (!order.length) return sceneAtTime(scenes, t);

  const eps = PLAYBACK_NARRATION_EPS;
  let activeEl = null;
  for (const el of order) {
    const ns = elementNarrationStartFromTimings(el, rendered);
    const ne = elementNarrationEnd(el, rendered);
    if (ns <= t + eps && t < ne) {
      const si = sceneIndexForBlock(el.id, scenes);
      if (si > 0 && t + eps < (Number(scenes[si].start) || 0)) continue;
      activeEl = el;
    }
  }
  if (activeEl) return sceneForBlockId(activeEl.id, scenes);

  let lastDone = null;
  for (const el of order) {
    if (elementNarrationEnd(el, rendered) <= t + eps) lastDone = el;
    else break;
  }
  if (lastDone) return sceneForBlockId(lastDone.id, scenes);

  for (let si = scenes.length - 1; si >= 0; si -= 1) {
    if (t + eps >= Number(scenes[si].start) || 0) return scenes[si];
  }
  return scenes[0] || sceneAtTime(scenes, t);
}

export function sceneIndexAtTime(scenes, t) {
  for (let i = 0; i < (scenes || []).length; i += 1) {
    const sc = scenes[i];
    if (t >= Number(sc.start) && t < Number(sc.end)) return i;
  }
  return Math.max(0, (scenes || []).length - 1);
}

const STACK_GAP = 10;
const LEFT_STACK_TYPES = new Set(['explanation', 'bullet', 'keyword']);
const MEDIA_TYPES = new Set(['image']);

function displayContentForMeasure(el) {
  const type = el?.type || '';
  const lv = elementListLevel(el);
  let text = el?.content || '';
  if (type === 'bullet' || type === 'keyword' || lv > 0) {
    const fmt = type === 'keyword' ? 'bullet' : type;
    text = formatListDisplay(text, lv, fmt);
  }
  return text;
}

export function estimateTextBlockHeight(text, maxWidth, type = 'explanation') {
  const fontSize = type === 'scene_title' ? 28 : 18;
  const lineH = Math.round(fontSize * 1.45);
  const charsPerLine = Math.max(12, Math.floor(maxWidth / (fontSize * 0.55)));
  const str = String(text || '');
  let lines = 0;
  str.split('\n').forEach((para) => {
    const p = para.trim();
    if (!p) {
      lines += 1;
      return;
    }
    lines += Math.max(1, Math.ceil(p.length / charsPerLine));
  });
  return Math.max(32, Math.ceil((Math.max(1, lines) * lineH + 14) * 1.12));
}

function leftColumnWidth(scene) {
  const cp = scene?.code_panel_layout;
  if (cp?.x != null) return Math.max(200, Number(cp.x) - 64 - 32);
  return 480;
}

function imageColumnX(scene) {
  const imgW = 480;
  const cp = scene?.code_panel_layout;
  if (cp?.x != null) return Math.max(64, Number(cp.x) - imgW - 24);
  return 720;
}

/** Re-bind diagram/code/console visibility to narration_start (mirrors backend repair). */
export function repairRenderedVisibility(rendered) {
  if (!rendered?.elements?.length) return rendered;
  const scenesByBid = {};
  for (const sc of rendered.scenes || []) {
    for (const bid of sc.block_ids || []) {
      scenesByBid[String(bid)] = sc;
    }
  }
  for (const el of rendered.elements) {
    if (!isNarrationGatedLayer(el)) continue;
    el.sync_visual_at_scene_start = false;
    const ns = elementNarrationStart(el);
    el.visible_from = ns;
    el.start = ns;
    const sc = scenesByBid[String(el.id)];
    if (sc && el.persist) {
      el.end = Math.max(Number(el.end) || 0, Number(sc.end) || ns);
    }
  }
  return rendered;
}

/** Client-side reflow — mirrors backend stack_text_layouts_in_scenes. */
export function reflowRenderedLayouts(rendered) {
  if (!rendered?.elements?.length || !rendered?.scenes?.length) return rendered;
  const byId = Object.fromEntries(
    (rendered.elements || []).filter((e) => e.id).map((e) => [String(e.id), e]),
  );
  (rendered.scenes || []).forEach((sc) => {
    const colW = leftColumnWidth(sc);
    const imgX = imageColumnX(sc);
    let yCursor = 140;
    let imgY = 200;
    let stackI = 0;
    (sc.block_ids || []).forEach((bid) => {
      const el = byId[String(bid)];
      if (!el) return;
      const t = (el.type || '').toLowerCase();
      const layout = { ...(el.layout || {}) };
      if (t === 'scene_title') {
        const h = estimateTextBlockHeight(displayContentForMeasure(el), colW, 'scene_title');
        Object.assign(layout, { x: 64, y: 48, w: 800, max_width: 1152, h: Math.max(56, h), stack_index: 0 });
        el.layout = layout;
        yCursor = 48 + layout.h + STACK_GAP;
        imgY = Math.max(imgY, yCursor);
        return;
      }
      if (LEFT_STACK_TYPES.has(t)) {
        const lv = elementListLevel(el);
        let x = 64;
        let maxW = colW;
        if (lv > 0) {
          const ind = applyListLayout({ x: 64, max_width: colW }, lv);
          x = ind.x;
          maxW = ind.max_width;
        }
        const h = estimateTextBlockHeight(displayContentForMeasure(el), maxW, t);
        Object.assign(layout, { x, y: yCursor, w: maxW, max_width: maxW, h, stack_index: stackI });
        el.layout = layout;
        yCursor += h + STACK_GAP;
        stackI += 1;
        return;
      }
      if (MEDIA_TYPES.has(t)) {
        imgY = Math.max(imgY, yCursor);
        const ih = Math.min(270, Math.max(120, Math.round(DESIGN_H * 0.36)));
        const iw = Math.min(480, Math.max(200, DESIGN_W - imgX - 48));
        Object.assign(layout, { x: imgX, y: imgY, w: iw, h: ih, max_width: iw, dynamic: true });
        el.layout = layout;
        imgY += ih + STACK_GAP;
      }
    });
  });
  syncRenderedFromNarrationTimings(rendered);
  return repairRenderedVisibility(rendered);
}

export function resolvedLayout(el) {
  const custom = el?.layout && typeof el.layout === 'object' ? el.layout : {};
  const base = ZONES[el?.type] || ZONES.explanation;
  const merged = { ...base, ...custom };
  const lv = elementListLevel(el);
  // Prefer server-computed y/h (wrap-aware stack); do not override with fixed line height.
  if (lv > 0 && (el?.type === 'bullet' || el?.type === 'explanation')) {
    merged.x = Number(base.x) || 64;
    merged.max_width = Number(base.max_width) || 480;
    return applyListLayout(merged, lv);
  }
  return merged;
}

export function codePanelLayoutForScene(scene) {
  if (scene?.code_panel_layout && typeof scene.code_panel_layout === 'object') {
    return { ...DEFAULT_CODE_PANEL, ...scene.code_panel_layout };
  }
  return { ...DEFAULT_CODE_PANEL };
}

/** All cumulative layers visible at time t within the active scene. */
export function pickRenderSet(elements, scenes, t, rendered = null) {
  const scene = sceneAtPlayhead(scenes, t, elements, rendered);
  if (!scene) return { draw: [], codeLines: [], highlightLine: null, scene: null, stateTables: [] };

  const allowed = new Set(scene.block_ids || []);
  const narratedOrder = narratedBlocksInOrder(elements, scenes);
  const draw = [];

  for (const el of elements || []) {
    if (!allowed.has(el.id)) continue;
    const type = el.type || '';
    if (type === 'code_step' || type === 'code_highlight' || type === 'scene_transition') continue;
    if (type === 'dry_run_state_table') continue;
    if (elementMayRenderAt(el, scene, t, narratedOrder, scenes, rendered)) draw.push(el);
  }

  const narratorCue = activeNarratorCueAtPlayhead(rendered, t);
  const filteredDraw = draw.filter((el) => {
    if (el.type !== 'flowchart_node') return true;
    const elPath = (el.node_path || []).join('/');
    const cuePath = narratorCue?.node_path || elPath;
    const maxStep = flowchartStepIndexAtPlayhead(rendered, t, cuePath);
    if (maxStep < 0) return elementMayRenderAt(el, scene, t, narratedOrder, scenes, rendered);
    const stepIdx = num(el.render_step_index, 999);
    return stepIdx <= maxStep;
  });

  filteredDraw.sort(
    (a, b) =>
      Number((a.layout || {}).stack_index ?? 999) - Number((b.layout || {}).stack_index ?? 999)
      || Number(a.start) - Number(b.start),
  );

  let highlightLine = null;
  for (const el of elements || []) {
    if (!allowed.has(el.id)) continue;
    if (el.type === 'code_highlight' && elementMayRenderAt(el, scene, t, narratedOrder, scenes, rendered)) {
      highlightLine = el.highlight_line;
    }
  }

  const sceneCode = (elements || [])
    .filter(
      (el) =>
        allowed.has(el.id)
        && el.type === 'code_step'
        && priorNarrationComplete(el, narratedOrder, t, rendered)
        && elementNarrationStartFromTimings(el, rendered) <= t,
    )
    .sort((a, b) => elementNarrationStart(a) - elementNarrationStart(b));

  let codeLines = [];
  for (const el of sceneCode) {
    const text = el.code_snippet || el.content || '';
    codeLines.push(...String(text).split('\n').filter((ln) => ln.length > 0 || codeLines.length === 0));
  }
  if (!codeLines.length && sceneCode.length) {
    codeLines = sceneCode.map((el) => el.code_snippet || el.content || '');
  }

  const stateTables = (elements || []).filter(
    (el) =>
      allowed.has(el.id)
      && el.type === 'dry_run_state_table'
      && elementMayRenderAt(el, scene, t, narratedOrder, scenes, rendered),
  );

  if (highlightLine == null) {
    for (let i = 0; i < sceneCode.length; i += 1) {
      const el = sceneCode[i];
      const ns = elementNarrationStart(el);
      const ne = elementNarrationEnd(el);
      if (ns <= t && t < ne) {
        highlightLine = i + 1;
        break;
      }
    }
  }

  if (rendered?.screenplay_events?.length) {
    const spLine = highlightLineAtTime(rendered, t);
    if (spLine != null) highlightLine = spLine;
  }

  const historyLine = codeHighlightFromNarratorHistory(rendered, t);
  if (historyLine != null) highlightLine = historyLine;

  if (historyLine != null && codeLines.length > historyLine) {
    codeLines = codeLines.slice(0, historyLine);
  }

  return { draw: filteredDraw, codeLines, highlightLine, scene, narrator_cue: narratorCue, stateTables };
}

function num(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

export function getEditorOverlays(rendered, playhead) {
  if (!rendered) return [];
  const fps = Number(rendered.fps) || 30;
  const { draw, codeLines, scene } = pickRenderSet(
    rendered.elements || [],
    rendered.scenes || [],
    playhead,
    rendered,
  );
  const items = [];

  draw.forEach((el) => {
    const layout = resolvedLayout(el);
    const isImage = el.type === 'image';
    const tRender = renderTimeForElement(el, playhead, fps);
    const age = tRender - elementNarrationStart(el);
    let opacity = 1;
    if (el.type === 'scene_title' && age > 4) opacity = 0.55;
    else if (['explanation', 'bullet'].includes(el.type) && age > 10) opacity = 0.88;

    items.push({
      id: el.id,
      kind: isImage ? 'image' : 'text',
      type: el.type,
      text:
        el.type === 'bullet' || elementListLevel(el) > 0
          ? formatListDisplay(el.content || '', elementListLevel(el), el.type)
          : (el.content || ''),
      image_url: el.image_url,
      x: Number(layout.x) || 64,
      y: Number(layout.y) || 140,
      w: Number(layout.w) || Number(layout.max_width) || 480,
      h: Number(layout.h) || 120,
      max_width: Number(layout.max_width) || 480,
      opacity,
    });
  });

  if (codeLines.length > 0) {
    const cp = codePanelLayoutForScene(scene);
    items.push({
      id: '__code_panel__',
      kind: 'code_panel',
      type: 'code_panel',
      codeLines,
      x: Number(cp.x) || 580,
      y: Number(cp.y) || 120,
      w: Number(cp.w) || 660,
      h: Math.min(Number(cp.h) || 400, codeLines.length * 28 + 40),
    });
  }

  return items;
}
